import type { CardHooks } from "./registry";

/**
 * Calculate Risk — Utility / Reaction.
 *
 * Supabase row classifies this as `cardType: "Utility"` with
 * `utilityFields.utilityType: "Reaction"` and ability text:
 *   "After you take damage you may play this card. Draw 2 cards, then discard 1."
 *
 * Wiring:
 *  - Card row sets `utilityFields.reactionTriggers: ["self-received-damage"]`
 *    so the engine's Action Chain machinery surfaces this card as an eligible
 *    response when the player takes damage. The existing reaction-window
 *    infrastructure (emitActionChainEvent, openReactionDialog, playUtility)
 *    handles eligibility, priority, and play-from-hand for free.
 *  - When played, `onUtilityPlay` fires here, returning:
 *      - `drawSelf: 2`  → engine draws 2 cards for the actor.
 *      - `prompt: pick-hand-discard count=1` → engine queues the discard
 *        prompt. applyResults sets the prompt BEFORE running drawSelf, but
 *        the prompt is only surfaced to the UI after applyResults returns,
 *        so by render time the 2 new cards are already in hand and eligible
 *        to discard.
 *
 * This card uses no per-round flag; reaction-card play frequency is gated
 * by the reaction window itself (you can only play it while a damage event
 * has just resolved). Stamina cost defaults to 0; if Noah adds a cost on
 * the Supabase row it will be deducted at play time by the engine.
 *
 * Data-driven adjustability:
 *  - Draw count (2) and discard count (1) are currently hard-coded here.
 *    If Noah wants to balance them later, expose `drawAmount` and
 *    `discardAmount` as numeric custom-fields on the card and read them
 *    from `ctx.card.utilityFields` or a new field.
 */

const DRAW_AMOUNT = 2;
const DISCARD_AMOUNT = 1;

export const calculateRisk: CardHooks = {
  onUtilityPlay: (ctx) => {
    const { self, card } = ctx;
    return {
      drawSelf: DRAW_AMOUNT,
      prompt: {
        kind: "pick-hand-discard" as const,
        playerNum: self.num as 1 | 2,
        selectedInstanceIds: [],
        discardCount: DISCARD_AMOUNT,
        sourceCardName: card.name,
      },
      log: [
        `${card.name} — drew ${DRAW_AMOUNT}, choose ${DISCARD_AMOUNT} to discard.`,
      ],
    };
  },
};
